import React, { useEffect, useMemo, useRef, useState } from 'react';
import CatalogEnhancement from './CatalogEnhancement.jsx';
import PosLiveBarcodeScanner from './PosLiveBarcodeScanner.jsx';
import {resolvePosScan,addScanWithStockGuard} from './posQuickCart.js';
import {discountCentsFromPercent,discountPercentValue,percentFromDiscountCents} from './discountPercent.js';
import {proposeClassification,findExistingProducts,productReadiness} from './productMaster.js';
import {suggestSku,productSaveIssues,fiscalCandidate,FISCAL_SOURCES,openingStockIssue} from './productIntake.js';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  Barcode,
  Boxes,
  Camera,
  Check,
  ChevronRight,
  CircleDollarSign,
  CloudOff,
  CreditCard,
  Edit3,
  FileClock,
  History,
  ImagePlus,
  LayoutDashboard,
  Loader2,
  LockKeyhole,
  Minus,
  PackagePlus,
  PauseCircle,
  Plus,
  Printer,
  QrCode,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  ShieldCheck,
  ShoppingCart,
  Store,
  Trash2,
  UserPlus,
  UsersRound,
  Wifi,
  X,
} from 'lucide-react';
import {
  addToCart,
  cartStockErrors,
  centsFromDecimal,
  decimalFromCents,
  detectBarcodeFromImage,
  expectedCash,
  isValidGtin,
  money,
  normalizeBarcode,
  paymentTotals,
  setLineDiscountPercent,
  setQuantity,
  stockFor,
  totals,
  translateMovement,
  validateProductPhoto,
} from './domain.js';
import {
  Button,
  EmptyState,
  Field,
  formatDateTime,
  Header,
  Modal,
  MoneyInput,
  Segmented,
  StatusBadge,
} from './ui.jsx';

const PAYMENT_LABELS = {
  cash: 'Dinheiro',
  pix: 'Pix externo',
  card: 'Cartão externo',
  other: 'Outro',
};

function productById(data, id) {
  return data.products.find((product) => product.id === id);
}

function capability(capabilities, key) {
  return Boolean(capabilities?.[key]);
}

function BarcodeCapture({ onDetected, compact = false, iconOnly = false }) {
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');

  async function scan(file) {
    if (!file) return;
    setScanning(true);
    setMessage('Lendo a fotografia…');
    try {
      const code = await detectBarcodeFromImage(file);
      if (!code) {
        setMessage(
          'Não consegui identificar. Aproxime o código, evite reflexos ou use a digitação/leitor físico.',
        );
        return;
      }
      onDetected(code);
      setMessage(`Código ${code} identificado. Confira antes de confirmar.`);
    } finally {
      setScanning(false);
    }
  }

  return (
    <div className={`barcodeCapture ${compact ? 'compact' : ''}`}>
      <label className="cameraButton">
        {scanning ? <Loader2 className="spin" size={18} /> : <Camera size={18} />}
        {!iconOnly && <span>{scanning ? 'Lendo…' : compact ? 'Câmera' : 'Fotografar código'}</span>}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(event) => {
            scan(event.target.files?.[0]);
            event.currentTarget.value = '';
          }}
        />
      </label>
      {message && <small>{message}</small>}
    </div>
  );
}

async function prepareProductPhoto(file) {
  const validation = validateProductPhoto(file);
  if (!validation.valid) throw new Error(validation.error);
  if (
    !['image/jpeg', 'image/png', 'image/webp'].includes(validation.type) ||
    typeof createImageBitmap !== 'function'
  ) {
    return file;
  }
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = Math.min(1, 1600 / longest);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d', { alpha: false }).drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.84));
    if (!blob) return file;
    const optimized = new File([blob], `produto-${Date.now()}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
    const optimizedValidation = validateProductPhoto(optimized);
    if (!optimizedValidation.valid) throw new Error(optimizedValidation.error);
    return optimized;
  } catch (error) {
    if (String(error?.message || '').includes('8 MB')) throw error;
    return file;
  } finally {
    bitmap?.close?.();
  }
}

function ProductPhotoCapture({ preview, onSelect, error }) {
  const [busy, setBusy] = useState(false);

  async function choose(file) {
    if (!file) return;
    setBusy(true);
    try {
      onSelect(await prepareProductPhoto(file), null);
    } catch (captureError) {
      onSelect(null, String(captureError?.message || captureError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="productPhotoCapture">
      <div className={`productPhotoPreview ${preview ? 'hasImage' : ''}`}>
        {preview ? <img src={preview} alt="Prévia do produto" /> : <ImagePlus size={30} />}
      </div>
      <div>
        <strong>Foto do produto</strong>
        <p>Usada para reconhecer o item no caixa e preservada no upgrade entre Repair e SmartPDV.</p>
        <label className="cameraButton">
          {busy ? <Loader2 className="spin" size={18} /> : <Camera size={18} />}
          <span>{busy ? 'Preparando…' : preview ? 'Fotografar novamente' : 'Fotografar produto'}</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            capture="environment"
            hidden
            onChange={(event) => {
              choose(event.target.files?.[0]);
              event.currentTarget.value = '';
            }}
          />
        </label>
        <small>Máximo 8 MB. Fotos compatíveis são otimizadas para WebP.</small>
        {error && <small className="photoError">{error}</small>}
      </div>
    </section>
  );
}

export function Dashboard({
  data,
  location,
  session,
  offlineCount,
  setScreen,
  capabilities,
  onVoid,
}) {
  const [voiding, setVoiding] = useState(null);
  const [reason, setReason] = useState('');
  const completed = data.sales.filter((sale) => sale.status === 'completed');
  const revenue = completed.reduce((sum, sale) => sum + Number(sale.total_cents || 0), 0);
  const average = completed.length ? Math.round(revenue / completed.length) : 0;
  const lowStock = data.products.filter(
    (product) =>
      product.active !== false &&
      product.track_inventory &&
      stockFor(data.balances, product.id, location?.id) <= Number(product.min_stock || 0),
  );
  const outOfStock = lowStock.filter((product) => stockFor(data.balances, product.id, location?.id) <= 0);

  return (
    <div className="admin">
      <Header
        eyebrow="OPERAÇÃO EM TEMPO REAL"
        title={`Olá, ${data.settings.display_name}`}
        description="Venda, estoque, caixa e integrações do tenant em uma única visão."
        action={
          <Button onClick={() => setScreen('pos')}>
            <ShoppingCart size={17} />
            Nova venda
          </Button>
        }
        meta={
          <div className="inlineBadges">
            <StatusBadge tone={session ? 'ok' : 'warning'}>
              {session ? 'Caixa aberto' : 'Caixa fechado'}
            </StatusBadge>
            {offlineCount > 0 && (
              <StatusBadge tone="warning">{offlineCount} operação(ões) na fila</StatusBadge>
            )}
          </div>
        }
      />

      <section className="metrics">
        <article className="metric">
          <CircleDollarSign />
          <div>
            <small>Vendas recentes</small>
            <strong>{money(revenue)}</strong>
            <span>{completed.length} registro(s)</span>
          </div>
        </article>
        <article className="metric">
          <ReceiptText />
          <div>
            <small>Ticket médio</small>
            <strong>{money(average)}</strong>
            <span>Recibos não fiscais</span>
          </div>
        </article>
        <article className="metric">
          <Boxes />
          <div>
            <small>Estoque baixo</small>
            <strong>{lowStock.length}</strong>
            <span>{outOfStock.length} sem estoque · {lowStock.length} abaixo do mínimo</span>
          </div>
        </article>
        <article className="metric">
          {offlineCount ? <CloudOff /> : <Wifi />}
          <div>
            <small>Continuidade</small>
            <strong>{offlineCount ? 'Pendente' : 'Sem fila local'}</strong>
            <span>{offlineCount ? 'Reenvio automático ativo' : 'Sem fila local'}</span>
          </div>
        </article>
      </section>

      <section className="dashboardGrid">
        <div className="panel">
          <div className="panelTitle">
            <div>
              <h2>Vendas recentes</h2>
              <p>Registro, pagamento declarado e situação.</p>
            </div>
            <Button kind="quiet" size="small" onClick={() => setScreen('audit')}>
              Ver auditoria
            </Button>
          </div>
          {data.sales.length ? (
            data.sales.slice(0, 10).map((sale) => (
              <div className="saleRow" key={sale.id}>
                <span className={`dot ${sale.status === 'completed' ? '' : 'cancelled'}`} />
                <div>
                  <strong>Venda #{sale.sale_number}</strong>
                  <small>
                    {formatDateTime(sale.created_at)} ·{' '}
                    {(sale.payment_summary || [])
                      .map((payment) => PAYMENT_LABELS[payment.method] || payment.method)
                      .join(' + ') || 'Pagamento não informado'}
                  </small>
                </div>
                <StatusBadge tone={sale.status === 'completed' ? 'ok' : 'danger'}>
                  {sale.status === 'completed' ? 'Concluída' : 'Cancelada'}
                </StatusBadge>
                <b>{money(sale.total_cents)}</b>
                {sale.status === 'completed' && capability(capabilities, 'void_sales') && (
                  <button
                    className="iconButton subtle"
                    title="Cancelar venda"
                    onClick={() => {
                      setVoiding(sale);
                      setReason('');
                    }}
                  >
                    <RotateCcw size={16} />
                  </button>
                )}
              </div>
            ))
          ) : (
            <EmptyState
              icon={ReceiptText}
              title="Nenhuma venda registrada"
              description="A primeira venda aparecerá aqui após a confirmação."
            />
          )}
        </div>

        <div className="panel attentionPanel">
          <div className="panelTitle">
            <div>
              <h2>Atenções operacionais</h2>
              <p>O que precisa de decisão antes do próximo turno.</p>
            </div>
          </div>
          {lowStock.slice(0, 6).map((product) => (
            <button key={product.id} onClick={() => setScreen('stock')}>
              <AlertTriangle size={17} />
              <span>
                <strong>{product.name}</strong>
                <small>
                  {stockFor(data.balances, product.id, location?.id) <= 0
                    ? 'Sem estoque'
                    : 'Estoque abaixo do mínimo'} ·{' '}
                  {stockFor(data.balances, product.id, location?.id)} {product.unit} · mínimo {product.min_stock}
                </small>
              </span>
              <ChevronRight size={16} />
            </button>
          ))}
          {!lowStock.length && (
            <EmptyState
              icon={Check}
              title="Operação sem alerta de estoque"
              description="Os itens controlados estão acima do mínimo configurado."
            />
          )}
        </div>
      </section>

      {voiding && (
        <Modal
          title={`Cancelar venda #${voiding.sale_number}`}
          subtitle="O estoque e o caixa serão revertidos na mesma transação."
          onClose={() => setVoiding(null)}
          footer={
            <>
              <Button kind="ghost" onClick={() => setVoiding(null)}>
                Voltar
              </Button>
              <Button
                kind="danger"
                disabled={reason.trim().length < 5}
                onClick={async () => {
                  await onVoid(voiding.id, reason);
                  setVoiding(null);
                }}
              >
                Confirmar cancelamento
              </Button>
            </>
          }
        >
          <Field label="Motivo obrigatório">
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Explique o motivo para a trilha de auditoria"
            />
          </Field>
        </Modal>
      )}
    </div>
  );
}

function CartLine({ item, setCart, allowDiscount, stockAvailable }) {
  const shortage = Boolean(item.track_inventory) && item.quantity > stockAvailable;
  const [discountOpen, setDiscountOpen] = useState(false);
  return (
    <article className="cartLine">
      <div className="cartLineMain">
        <span>{item.name.slice(0, 2).toUpperCase()}</span>
        <div>
          <strong>{item.name}</strong>
          <small>
            {item.sku} · {money(item.unit_price_cents)}
          </small>
        </div>
        <b>{money(Math.round(item.quantity * item.unit_price_cents) - item.discount_cents)}</b>
      </div>
      {shortage && <small className="cartStockWarning" role="status">
        <AlertTriangle size={14} />
        {stockAvailable <= 0 ? 'Sem estoque' : 'Estoque insuficiente'} ·
        saldo registrado: {stockAvailable} {item.unit || 'UN'}
      </small>}
      <div className="qty">
        <button onClick={() => setCart((cart) => setQuantity(cart, item.product_id, item.quantity - 1))}>
          <Minus size={15} />
        </button>
        <input
          type="number"
          value={item.quantity}
          min="0"
          step={item.allow_fractional ? '.001' : '1'}
          onChange={(event) =>
            setCart((cart) => setQuantity(cart, item.product_id, event.target.value))
          }
        />
        <button onClick={() => setCart((cart) => setQuantity(cart, item.product_id, item.quantity + 1))}>
          <Plus size={15} />
        </button>
        {allowDiscount && (
          <button
            className={discountOpen ? 'active' : ''}
            onClick={() => setDiscountOpen((current) => !current)}
            title="Desconto do item"
          >
            %
          </button>
        )}
        <button
          className="remove"
          onClick={() => setCart((cart) => setQuantity(cart, item.product_id, 0))}
          title="Remover"
        >
          <X size={15} />
        </button>
      </div>
      {discountOpen && (
        <Field label="Desconto do item (%)">
          <div className="percentInput">
            <input type="number" min="0" max="100" step="0.01" inputMode="decimal"
              aria-label="Desconto percentual do item"
              value={item.discount_percent ?? percentFromDiscountCents(
                Math.round(item.quantity*item.unit_price_cents),item.discount_cents)}
              onChange={(event)=>setCart((current)=>setLineDiscountPercent(
                current,item.product_id,event.target.value))}/>
            <span>%</span>
          </div>
        </Field>
      )}
    </article>
  );
}

export function Pos({
  data,
  cart,
  setCart,
  session,
  location,
  terminal,
  org,
  capabilities,
  heldCarts,
  onHold,
  onResume,
  onCheckout,
  setScreen,
  onCreateFromScan,
  operatorMode = false,
  presaleOnly = false,
  onCatalogSearch = null,
}) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanFeedback, setScanFeedback] = useState('');
  const [catalogExpanded,setCatalogExpanded]=useState(false);
  const [catalogLimit,setCatalogLimit]=useState(36);
  const [heldOpen, setHeldOpen] = useState(false);
  const [customer, setCustomer] = useState('');
  const [orderDiscountPercent, setOrderDiscountPercent] = useState('0');
  const [notes, setNotes] = useState('');
  const [payments, setPayments] = useState([{ method: 'cash', value: '0,00' }]);
  const [busy, setBusy] = useState(false);
  const [checkoutError,setCheckoutError] = useState('');
  const searchRef = useRef(null);
  useEffect(() => {
    if(!presaleOnly || typeof onCatalogSearch !== 'function')return undefined;
    const searchTerm=search.trim();
    if(searchTerm.length<2)return undefined;
    const handle=setTimeout(()=>Promise.resolve(onCatalogSearch(searchTerm)).catch(()=>{}),300);
    return ()=>clearTimeout(handle);
  },[presaleOnly,onCatalogSearch,search]);

  const subtotalBeforeOrderDiscount=totals(cart,0).subtotalCents;
  const orderDiscountCents=discountCentsFromPercent(subtotalBeforeOrderDiscount,orderDiscountPercent);
  const summary = totals(cart,orderDiscountCents);
  const allowNegative = Boolean(data.settings.operations?.allow_negative_stock);
  const canStageOutOfStock = operatorMode && capability(capabilities, 'sell');
  const stockErrors = cartStockErrors(cart, data.balances, location?.id, allowNegative);
  const stockShortages = cart.filter((item) =>
    item.track_inventory &&
    item.quantity > stockFor(data.balances, item.product_id, location?.id),
  );
  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return data.products.filter(
      (product) =>
        product.active &&
        (category === 'all' || product.category_id === category) &&
        (!query ||
          [product.name, product.sku, product.barcode, product.brand]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query))),
    );
  }, [data.products, category, search]);
  const hasSearch=Boolean(search.trim());
  // Cashier-first: do not place the full catalog between barcode search and cart.
  // Manual typing shows matches; an explicit action opens the browse catalog.
  const showCatalog=hasSearch||catalogExpanded;
  const productsToShow=hasSearch?filteredProducts.slice(0,6):filteredProducts.slice(0,catalogLimit);
  useEffect(()=>{setCatalogLimit(36)},[category,search]);

  const paymentRows = payments.map((payment) => ({
    method: payment.method,
    amount_cents: centsFromDecimal(payment.value),
  }));
  const paymentSummary = paymentTotals(paymentRows, summary.totalCents);
  const discountPercent = summary.subtotalCents
    ? (summary.discountCents * 100) / summary.subtotalCents
    : 0;
  const maxDiscount = Number(data.settings.operations?.max_cashier_discount_percent || 0);
  const customerRequired = Boolean(data.settings.operations?.require_customer);
  const discountAllowed =
    discountPercent <= maxDiscount || capability(capabilities, 'discount_override');

  function addProduct(product) {
    const stock = stockFor(data.balances, product.id, location?.id);
    if (product.track_inventory && stock <= 0 && !allowNegative && !canStageOutOfStock) return;
    setCart((current) => addToCart(current, presaleOnly && product.organization_name ? {...product,name:product.organization_name+' · '+product.name} : product));
    setSearch('');
    const shortStock = product.track_inventory && stock <= 0;
    setScanFeedback(shortStock
      ? 'Item sem estoque registrado adicionado para conferência. Regularize o saldo antes de concluir a venda, salvo autorização de estoque negativo.'
      : '');
    setCatalogExpanded(false);
  }

  function useBarcode(raw){
    const match=resolvePosScan(data.products,raw);
    if(presaleOnly&&match.status==='missing'&&typeof onCatalogSearch==='function')Promise.resolve(onCatalogSearch(String(raw||'').trim())).catch(()=>{});
    if(match.status==='found'){
      const result=addScanWithStockGuard(cart,match.product,data.balances,
        location?.id,allowNegative,canStageOutOfStock);
      if(result.status==='added'){
        setCart(result.cart);setSearch('');setCatalogExpanded(false);
        setScanFeedback(result.insufficientStock
          ? `${match.product.name} · ${result.quantity} no carrinho · Sem estoque registrado. Conferir saldo antes de receber.`
          : `${match.product.name} · ${result.quantity} ${match.product.unit||'UN'} no carrinho.`);
        searchRef.current?.focus();
        return true;
      }
      setScanFeedback(result.status==='out_of_stock'?'Estoque insuficiente.':
        result.status==='unpriced'?'Produto sem preço válido. Revise o cadastro.':'Não foi possível adicionar.');
    }else{
      setScanFeedback(match.status==='missing'?'Produto ainda não cadastrado neste catálogo.':
        match.status==='ambiguous'?'Código duplicado no catálogo. Revise o cadastro.':'');
    }
    // Keep human names and accents intact; normalize only barcode-like digits.
    const entered=String(raw||'').trim();
    setSearch(/^[\d\s.\-]+$/.test(entered)?(match.code||''):entered);
    return false;
  }

  function acceptLiveBarcode(raw) {
    const match=resolvePosScan(data.products,raw);
    if(presaleOnly&&match.status==='missing'&&typeof onCatalogSearch==='function')Promise.resolve(onCatalogSearch(String(raw||'').trim())).catch(()=>{});
    if(match.status==='empty')return;
    setCategory('all');
    // The scanner closes after one detection; do not mutate prices or inventory.
    if(match.status==='found'){
      const result=addScanWithStockGuard(cart,match.product,data.balances,
        location?.id,allowNegative,canStageOutOfStock);
      if(result.status==='added'){
        setCart(result.cart);
        setSearch('');
        setCatalogExpanded(false);
        setScanFeedback(result.insufficientStock
          ? `${match.product.name} · ${result.quantity} no carrinho · Sem estoque registrado. Conferir saldo antes de receber.`
          : `${match.product.name} · ${result.quantity} ${match.product.unit||'UN'} no carrinho.`);
        return;
      }
      const reason={
        out_of_stock:'Sem estoque suficiente para adicionar outra unidade.',
        unpriced:'Produto sem preço válido. Revise o cadastro.',
        cart_full:'Carrinho no limite de itens.',
        unavailable:'Produto indisponível.',
      };
      setSearch(match.code);
      setScanFeedback(reason[result.status]||'Não foi possível adicionar o produto.');
      return;
    }
    setSearch(match.code);
    setScanFeedback(match.status==='ambiguous'
      ? 'Código vinculado a mais de um produto. Selecione manualmente após revisar o cadastro.'
      : 'Produto ainda não cadastrado neste catálogo.');
  }

  function openCheckout() {
    setCheckoutError('');
    if(!presaleOnly)setPayments([{ method: 'cash', value: decimalFromCents(summary.totalCents) }]);
    setCheckoutOpen(true);
  }

  async function finish() {
    setBusy(true);
    try {
      const result = await onCheckout({
        items: cart,
        payments: presaleOnly ? [] : paymentRows,
        customerId: presaleOnly ? null : customer,
        discountCents: presaleOnly ? 0 : orderDiscountCents,
        notes,
      });
      if (result) {
        setCheckoutOpen(false);
        setCustomer('');
        setOrderDiscountPercent('0');
        setNotes('');
        setCheckoutError('');
      }
    } catch(error) {
      if(!presaleOnly)throw error;
      // Keep cart and the idempotency key intact; the server may still have saved it.
      setCheckoutError(String(error?.message||'Não foi possível salvar a pré-venda.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pos">
      <section className="productBrowser">
        <div className="pageTitle">
          <div>
            <p className="eyebrow">
              {location?.name} · {terminal?.name}
            </p>
            <h1>{presaleOnly ? 'Frente de caixa · pré-venda' : 'Nova venda'}</h1>
          </div>
          <StatusBadge tone={session ? 'ok' : 'danger'}>
            {presaleOnly ? 'Somente pré-vendas não fiscais' : session ? 'Caixa aberto' : 'Caixa fechado'}
          </StatusBadge>
        </div>

        <div className="scanBar posDirectScanBar">
          <div className="search posSearchInput">
            <Search size={19} aria-hidden="true" />
            <input
              ref={searchRef}
              aria-label="Buscar por nome, SKU ou código de barras"
              value={search}
              onChange={(event) => {setSearch(event.target.value);setCategory('all');setScanFeedback('')}}
              onKeyDown={(event) => {
                if (event.key === 'Enter') useBarcode(search);
              }}
              placeholder="Nome, SKU ou código de barras"
              autoComplete="off"
            />
            <button type="button" className="posBarcodeTrigger"
              aria-label="Abrir câmera para ler código de barras automaticamente"
              title="Ler código de barras"
              onClick={() => {setScanFeedback('');setScannerOpen(true)}}>
              <Barcode size={23} />
            </button>
          </div>
          {scanFeedback&&<div className="posScanFeedback" role="status">
            <span>{scanFeedback}</span>
            {search&&resolvePosScan(data.products,search).status==='missing'&&capability(capabilities,'catalog')&&
              <button type="button" onClick={()=>onCreateFromScan?.(search)}>
                <Plus size={15}/> Cadastrar
              </button>}
          </div>}
        </div>


        {showCatalog && (
          <>
            {!hasSearch&&(
              <>
                <div className="posBrowseHead">
                  <strong>Consultar catálogo</strong>
                  <button type="button" onClick={()=>setCatalogExpanded(false)}
                    aria-label="Fechar catálogo e voltar ao carrinho">
                    Fechar catálogo <X size={16}/>
                  </button>
                </div>
        <div className="categoryChips">
          <button className={category === 'all' ? 'active' : ''} onClick={() => setCategory('all')}>
            Todos
          </button>
          {data.categories
            .filter((item) => item.active && !item.parent_id)
            .map((item) => (
              <button
                key={item.id}
                className={category === item.id ? 'active' : ''}
                onClick={() => setCategory(item.id)}
              >
                {item.name}
              </button>
            ))}
        </div>
              </>
            )}
            {hasSearch&&<div className="posResultInfo">
              <strong>{filteredProducts.length===1?'1 produto encontrado':`${filteredProducts.length} produtos encontrados`}</strong>
              <button type="button" onClick={()=>{setSearch('');setCatalogExpanded(false);searchRef.current?.focus()}}>
                Limpar busca <X size={14}/>
              </button>
            </div>}
        <div className={`productTiles ${hasSearch?'posSearchResults':''}`}>
          {productsToShow.map((product) => {
            const stock = stockFor(data.balances, product.id, location?.id);
            const unavailable = product.track_inventory && stock <= 0;
            const cannotStage = unavailable && !canStageOutOfStock && !allowNegative;
            return (
              <button
                className={`productTile ${unavailable ? 'outOfStock' : ''}`}
                key={product.id}
                title={unavailable ? (canStageOutOfStock ? 'Sem estoque registrado. Operador pode adicionar ao carrinho para conferência.' : 'Produto sem estoque disponível.') : undefined}
                disabled={cannotStage}
                onClick={() => addProduct(product)}
              >
                {product.image_url ? (
                  <img className="productTileImage" src={product.image_url} alt="" />
                ) : (
                  <span>{product.name.slice(0, 2).toUpperCase()}</span>
                )}
                <small>{presaleOnly&&product.organization_name?product.organization_name+' · '+product.sku:product.sku}</small>
                <strong>{product.name}</strong>
                <b>{money(product.price_cents)}</b>
                {!cannotStage&&<em className="posTileAdd"><Plus size={13}/> Adicionar</em>}
                <i className={unavailable?'outOfStockBadge':''}>
                  {unavailable ? 'Sem estoque' : product.track_inventory ? `${stock} ${product.unit}` : 'Sem controle'}
                </i>
              </button>
            );
          })}
        </div>
            {hasSearch&&filteredProducts.length===0&&
              <p className="posNoResults">Não encontramos este item no catálogo. Confira o código ou cadastre o produto, se tiver permissão.</p>}
            {hasSearch&&filteredProducts.length>6&&
              <p className="posMoreResults">Exibindo 6 de {filteredProducts.length} produtos. Digite mais detalhes para refinar.</p>}
            {!hasSearch&&filteredProducts.length>catalogLimit&&
              <div className="posCatalogMore">
                <span>Exibindo {productsToShow.length} de {filteredProducts.length} produtos</span>
                <button type="button" onClick={()=>setCatalogLimit(n=>n+36)}>
                  Ver mais produtos <ChevronRight size={17}/>
                </button>
              </div>}
          </>
        )}

      </section>

      <aside className="cart">
        <header>
          <div>
            <p className="eyebrow">CARRINHO ATUAL</p>
            <h2>{cart.length ? `${cart.length} item(ns)` : presaleOnly?'Carrinho vazio':'Venda vazia'}</h2>
          </div>
          <div className="cartHeaderActions">
            {!hasSearch && !catalogExpanded && (
              <button type="button" className="posCatalogAction"
                onClick={()=>{setCatalogExpanded(true);setCategory('all')}}
                aria-label="Consultar catálogo de produtos"><Boxes size={17}/> Catálogo</button>
            )}
            {heldCarts.length > 0 && (
              <button className="iconButton" onClick={() => setHeldOpen(true)} title="Vendas suspensas">
                <History size={17} />
                <i>{heldCarts.length}</i>
              </button>
            )}
            {cart.length > 0 && (
              <button className="iconButton" onClick={() => setCart([])} title="Limpar carrinho">
                <Trash2 size={17} />
              </button>
            )}
          </div>
        </header>

        <div className="cartLines">
          {cart.length ? (
            cart.map((item) => (
              <CartLine
                key={item.product_id}
                item={item}
                setCart={setCart}
                allowDiscount={maxDiscount > 0 || capability(capabilities, 'discount_override')}
                stockAvailable={stockFor(data.balances, item.product_id, location?.id)}
              />
            ))
          ) : (
            <EmptyState
              icon={ShoppingCart}
              title="Pronto para vender"
              description="Bipe um código no leitor, escaneie pela câmera ou busque pelo nome acima."
            />
          )}
        </div>

        <div className="cartControls">
          <Field label="Cliente">
            <select value={customer} onChange={(event) => setCustomer(event.target.value)}>
              <option value="">Consumidor não identificado</option>
              {data.customers.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </Field>
          {!presaleOnly && (maxDiscount > 0 || capability(capabilities, 'discount_override')) && (
            <Field label={`Desconto da venda (%) · limite operador ${maxDiscount}%`}>
              <div className="percentInput">
                <input type="number" min="0" max="100" step="0.01" inputMode="decimal"
                  aria-label="Desconto percentual da venda"
                  value={orderDiscountPercent}
                  onChange={(event)=>setOrderDiscountPercent(
                    String(discountPercentValue(event.target.value)))}/>
                <span>%</span>
              </div>
              <small>Equivalente: {money(orderDiscountCents)}</small>
            </Field>
          )}
        </div>

        <div className="cartSummary">
          <div>
            <span>Subtotal</span>
            <b>{money(summary.subtotalCents)}</b>
          </div>
          <div>
            <span>Desconto</span>
            <b>- {money(summary.discountCents)}</b>
          </div>
          <div className="total">
            <span>Total</span>
            <b>{money(summary.totalCents)}</b>
          </div>
          {!discountAllowed && (
            <p className="inlineError">Desconto acima do limite do operador.</p>
          )}
          {stockShortages.length > 0 && (
            <p className="inlineError" role="status">
              <AlertTriangle size={14} /> Estoque insuficiente: {stockShortages.map((item) => item.name).join(', ')}.
              {allowNegative
                ? ' Estoque negativo permitido pela configuração da loja.'
                : ' Pode permanecer no carrinho, mas regularize o saldo para habilitar Receber.'}
            </p>
          )}
          {!presaleOnly && customerRequired && !customer && (
            <p className="inlineError">Selecione um cliente para concluir esta venda.</p>
          )}
        </div>

        <div className="cartActions">
          {!presaleOnly && cart.length > 0 && (
            <Button
              kind="ghost"
              onClick={() => {
                onHold({
                  cart,
                  customer_id: customer,
                  discount_cents: orderDiscountCents,
                  discount_percent: discountPercentValue(orderDiscountPercent),
                });
                setCart([]);
                setCustomer('');
                setOrderDiscountPercent('0');
              }}
            >
              <PauseCircle size={17} />
              Suspender
            </Button>
          )}
          {session || presaleOnly ? (
            <button
              className="checkout"
              disabled={
                !cart.length ||
                !discountAllowed ||
                (!presaleOnly && stockErrors.length > 0) ||
                (!presaleOnly && customerRequired && !customer)
              }
              onClick={openCheckout}
            >
              <span>{presaleOnly ? 'Salvar pré-venda' : 'Receber'}</span>
              <strong>{money(summary.totalCents)}</strong>
              <ChevronRight size={21} />
            </button>
          ) : (
            <button className="checkout warning" onClick={() => setScreen('cash')}>
              <span>Abrir caixa para vender</span>
              <ChevronRight size={21} />
            </button>
          )}
        </div>
        {presaleOnly && <p className="nonFiscal" role="note">Pré-venda interna, sem pagamento, baixa de estoque, caixa ou emissão fiscal. Produtos identificados por empresa.</p>}
        <small className="nonFiscal">
          <ReceiptText size={14} />
          Recibo não fiscal · tenant {org.slug}
        </small>
      </aside>

      {scannerOpen&&<PosLiveBarcodeScanner
        onClose={()=>setScannerOpen(false)}
        onDetected={acceptLiveBarcode}
      />}


      {heldOpen && (
        <Modal
          title="Vendas suspensas"
          subtitle="Carrinhos locais preservados neste dispositivo."
          onClose={() => setHeldOpen(false)}
          footer={
            <Button kind="ghost" onClick={() => setHeldOpen(false)}>
              Fechar
            </Button>
          }
        >
          <div className="heldList">
            {heldCarts.map((held) => (
              <article key={held.id}>
                <PauseCircle size={20} />
                <div>
                  <strong>{held.label}</strong>
                  <small>
                    {held.cart.length} item(ns) · {formatDateTime(held.created_at)}
                  </small>
                </div>
                <Button
                  size="small"
                  onClick={() => {
                    onResume(held);
                    setCustomer(held.customer_id || '');
                    setOrderDiscountPercent(String(held.discount_percent ??
                      percentFromDiscountCents(totals(held.cart,0).subtotalCents,held.discount_cents)));
                    setHeldOpen(false);
                  }}
                >
                  Retomar
                </Button>
              </article>
            ))}
            {!heldCarts.length && (
              <EmptyState icon={PauseCircle} title="Nenhuma venda suspensa" />
            )}
          </div>
        </Modal>
      )}

      {checkoutOpen && (
        <Modal
          wide
          title={presaleOnly ? 'Confirmar pré-venda não fiscal' : 'Concluir venda'}
          subtitle={presaleOnly ? 'Somente registro interno pendente, sem pagamento ou documento fiscal.' : 'Pagamentos externos são declarados; a captura K‑Pay permanece bloqueada.'}
          onClose={() => setCheckoutOpen(false)}
          footer={
            <>
              <Button kind="ghost" onClick={() => setCheckoutOpen(false)}>
                Voltar
              </Button>
              <Button
                disabled={
                  busy ||
                  (!presaleOnly && !paymentSummary.valid) ||
                  !discountAllowed ||
                  (!presaleOnly && stockErrors.length > 0) ||
                  (!presaleOnly && customerRequired && !customer)
                }
                onClick={finish}
              >
                {busy ? <Loader2 className="spin" size={17} /> : <ShieldCheck size={17} />}
                {busy ? 'Salvando…' : presaleOnly ? `Salvar pré-venda · ${money(summary.totalCents)}` : `Finalizar · ${money(summary.totalCents)}`}
              </Button>
            </>
          }
        >
          {presaleOnly ? <div className="panel">
            <p>Itens e preços serão revalidados pelo servidor antes da gravação.</p>
            <strong>{money(summary.totalCents)}</strong>
            <p>Não recebe pagamentos nem emite nota fiscal.</p>
            {checkoutError&&<p className="inlineError" role="alert">{checkoutError}</p>}
          </div> : <div className="checkoutGrid">
            <section>
              <div className="payTotal">
                <small>TOTAL NÃO FISCAL</small>
                <strong>{money(summary.totalCents)}</strong>
                <span>
                  {cart.length} item(ns) · {customer ? 'cliente identificado' : 'consumidor'}
                </span>
              </div>
              <Field label="Observação da venda">
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Opcional"
                />
              </Field>
            </section>
            <section className="paymentEditor">
              <div className="paymentEditorTitle">
                <div>
                  <h3>Formas de pagamento</h3>
                  <p>Permite dinheiro, Pix, cartão ou combinação.</p>
                </div>
                <Button
                  kind="quiet"
                  size="small"
                  onClick={() =>
                    setPayments((current) => [
                      ...current,
                      { method: 'pix', value: decimalFromCents(summary.totalCents) },
                    ])
                  }
                >
                  <Plus size={15} />
                  Dividir
                </Button>
              </div>
              {payments.map((payment, index) => (
                <div className="paymentLine" key={index}>
                  <select
                    value={payment.method}
                    onChange={(event) =>
                      setPayments((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, method: event.target.value } : item,
                        ),
                      )
                    }
                  >
                    {Object.entries(PAYMENT_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <MoneyInput
                    value={payment.value}
                    onChange={(value) =>
                      setPayments((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, value } : item,
                        ),
                      )
                    }
                  />
                  {payments.length > 1 && (
                    <button
                      className="iconButton subtle"
                      onClick={() =>
                        setPayments((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <div className="paymentSummary">
                <div>
                  <span>Informado</span>
                  <b>{money(paymentSummary.tenderedCents)}</b>
                </div>
                <div>
                  <span>Troco</span>
                  <b>{money(paymentSummary.changeCents)}</b>
                </div>
              </div>
              {!paymentSummary.valid && (
                <p className="inlineError">
                  Informe pagamentos suficientes. Troco só pode sair do valor em dinheiro.
                </p>
              )}
              <p className="legal">
                Pix e cartão registram declaração de recebimento externo. Nenhuma captura financeira
                é executada nesta versão.
              </p>
            </section>
          </div>}
        </Modal>
      )}
    </div>
  );
}

const EMPTY_PRODUCT = {
  id: '',
  name: '',
  description: '',
  brand: '',
  supplier: '',
  sku: '',
  barcode: '',
  category_id: '',
  unit: 'UN',
  price: '0,00',
  cost: '0,00',
  track_inventory: true,
  allow_fractional: false,
  min_stock: 0,
  opening_stock: '0',
  active: true,
  tax_code_ncm: '',
  tax_code_cest: '',
  tax_origin: 0,
  image_path: null,
  image_mime_type: null,
  image_url: null,
  metadata: {},
};

function productToForm(product) {
  if (!product) return { ...EMPTY_PRODUCT };
  return {
    ...EMPTY_PRODUCT,
    ...product,
    price: decimalFromCents(product.price_cents),
    cost: decimalFromCents(product.cost_cents),
    opening_stock: '0',
    tax_code_ncm: product.tax_code_ncm || '',
    tax_code_cest: product.tax_code_cest || '',
  };
}

export function Products({ data, capabilities, onSave, onIdentify, onEnsureCategory, onEnsureSubcategory, location, entitlement={}, initialBarcode=null, onConsumeInitialBarcode }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PRODUCT });
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [extraPhotos, setExtraPhotos] = useState([]);
  const [categoryBusy,setCategoryBusy]=useState(false);
  const [categoryName,setCategoryName]=useState('');
  const [formError,setFormError]=useState('');
  const [fiscalAcknowledged,setFiscalAcknowledged]=useState(false);
  const [subcategoryName,setSubcategoryName]=useState('');
  const [subBusy,setSubBusy]=useState(false);
  const [mobileStep,setMobileStep]=useState(0);
  const wizardLabels=['Fotos e IA','Identificação','Preço e estoque','Revisão e extras'];
  function goMobileStep(step){
    if(document.activeElement instanceof HTMLElement && ['INPUT','TEXTAREA','SELECT'].includes(document.activeElement.tagName))
      document.activeElement.blur();
    setMobileStep(Math.max(0,Math.min(3,step)));
    window.requestAnimationFrame(()=>document.querySelector('.productWizardModal .modalBody')?.scrollTo({top:0,behavior:'smooth'}));
  }
  const canEdit = capability(capabilities, 'catalog');
  const filtered = data.products.filter((product) =>
    [product.name, product.sku, product.barcode, product.brand]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(search.toLowerCase())),
  );
  const computedSku=suggestSku(form,data.products,form.id);
  const saveIssues=productSaveIssues(form,data.products);
  const isCreating=editing==='new';
  const canAdjustStock=capability(capabilities,'inventory');
  const openingIssue=isCreating&&form.track_inventory
    ?openingStockIssue(form.opening_stock,Boolean(form.allow_fractional),canAdjustStock):'';
  const openingQty=isCreating&&form.track_inventory
    ?Number(String(form.opening_stock||'0').replace(',','.')):0;
  const categorySuggestion=String(form.metadata?.commerce?.category_suggestion||'').trim();
  const roots=data.categories.filter(c=>!c.parent_id);
  const suggestedCategoryExists=roots.some(c=>c.name.toLocaleLowerCase('pt-BR')===categorySuggestion.toLocaleLowerCase('pt-BR'));
  const selectedSubcategory=form.metadata?.classification?.subcategory_id||'';
  const subcategories=data.categories.filter(c=>c.parent_id===form.category_id&&c.active);
  const suggestedSubcategory=String(form.metadata?.classification?.subcategory_suggestion||'').trim();
  const suggestedSubcategoryExists=subcategories.some(c=>c.name.toLocaleLowerCase('pt-BR')===suggestedSubcategory.toLocaleLowerCase('pt-BR'));
  const readiness=productReadiness({...form,price_cents:centsFromDecimal(form.price)},data.channelConnections||[],entitlement,{stagedPhoto:Boolean(photoFile)});
  const matchedProducts=findExistingProducts(data.products,form,form.id);
  const fiscal=fiscalCandidate(form);
  const gtinWarning =
    form.barcode &&
    /^\d+$/.test(form.barcode) &&
    [8, 12, 13, 14].includes(form.barcode.length) &&
    !isValidGtin(form.barcode);

  function open(product) {
    setEditing(product?.id || 'new');
    setForm(productToForm(product));
    setPhotoFile(null);
    setPhotoPreview(product?.image_url || '');
    setExtraPhotos((product?.metadata?.gallery_images || []).filter(i=>i.path).slice(0,2).map(i=>({path:i.path,preview:i.image_url||'',mime_type:i.mime_type})));
    setPhotoError('');
    setCategoryName('');setCategoryBusy(false);setFormError('');setFiscalAcknowledged(false);setMobileStep(0);
    setSubcategoryName('');setSubBusy(false);
  }

  useEffect(()=>{
    if(!initialBarcode?.barcode||!canEdit)return;
    open(null);
    const barcode=normalizeBarcode(initialBarcode.barcode);
    setForm(current=>({...current,barcode}));
    onConsumeInitialBarcode?.();
  },[initialBarcode?.token,canEdit]);

  function closeEditor() {
    extraPhotos.forEach(i=>{if(i.preview?.startsWith('blob:'))URL.revokeObjectURL(i.preview)});
    setExtraPhotos([]);
    setEditing(null);
    setPhotoFile(null);
    setPhotoPreview('');
    setPhotoError('');
  }

  useEffect(
    () => () => {
      if (photoPreview.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview],
  );

  function selectPhoto(file, error) {
    if (error) {
      setPhotoError(error);
      return;
    }
    if (!file) return;
    setPhotoError('');
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function ensureCategory(name){
    if(!name?.trim()||!onEnsureCategory)return;
    setCategoryBusy(true);setFormError('');
    try {
      const result=await onEnsureCategory(name.trim());
      if(!result?.id)throw new Error('A categoria não foi criada.');
      setForm(current=>({...current,category_id:result.id}));
      setCategoryName('');
    }catch(error){setFormError(String(error?.message||error))}
    finally{setCategoryBusy(false)}
  }
  async function ensureSubcategory(name){
    if(!form.category_id||!onEnsureSubcategory||!name?.trim())return;
    setSubBusy(true);setFormError('');
    try{
      const child=await onEnsureSubcategory(form.category_id,name);
      if(!child?.id)throw new Error('Subcategoria não criada.');
      setForm(current=>({...current,metadata:{
        ...(current.metadata||{}),
        classification:{...(current.metadata?.classification||{}),
          subcategory_id:child.id,subcategory_suggestion:name,reviewed_at:new Date().toISOString()}
      }}));
      setSubcategoryName('');
    }catch(error){setFormError(String(error?.message||error))}
    finally{setSubBusy(false)}
  }
  function applyFiscalCandidate(){
    if(!fiscal||!fiscalAcknowledged)return;
    setForm(current=>({...current,
      tax_code_ncm:current.tax_code_ncm||fiscal.ncm,
      tax_code_cest:current.tax_code_cest||fiscal.cest,
      metadata:{...(current.metadata||{}),fiscal_research:{
        ncm:fiscal.ncm,cest:fiscal.cest,sources:fiscal.sources,
        source_date:fiscal.as_of,review_state:'pending_fiscal_validation',
        acknowledged_by_operator_at:new Date().toISOString()
      }}
    }));
  }
  async function save() {
    if(saveIssues.length||gtinWarning||openingIssue){setFormError([...saveIssues,...(gtinWarning?['GTIN com dígito verificador inválido.']:[]),...(openingIssue?[openingIssue]:[])].join(' '));return}
    setFormError('');setBusy(true);
    try {
      await onSave({
        ...form,
        sku:computedSku,
        barcode: normalizeBarcode(form.barcode) || null,
        price_cents: centsFromDecimal(form.price),
        cost_cents: centsFromDecimal(form.cost),
        min_stock: Number(form.min_stock || 0),
        tax_origin: Number(form.tax_origin || 0),
      }, photoFile, extraPhotos, openingQty);
      closeEditor();
    } catch (error) {
      setFormError(String(error?.message||error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin">
      <Header
        eyebrow="CATÁLOGO"
        title="Produtos e preços"
        description="Cadastro preservado entre Repair e SmartPDV, com identidade fiscal desacoplada."
        action={
          canEdit && (
            <Button onClick={() => open(null)}>
              <Plus size={17} />
              Novo produto
            </Button>
          )
        }
      />
      <label className="sectionSearch">
        <Search size={18} />
        <input
          placeholder="Buscar nome, SKU, marca ou código"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <section className="panel responsiveTable productCatalogTable">
        <table>
          <thead>
            <tr>
              <th>Produto</th>
              <th>SKU / código</th>
              <th>Preço</th>
              <th>Estoque</th>
              <th>Tributário</th>
              <th>Status</th>
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {filtered.map((product) => (
              <tr key={product.id}>
                <td data-label="Produto">
                  <div className="productIdentity">
                    {product.image_url ? (
                      <img src={product.image_url} alt="" />
                    ) : (
                      <span>{product.name.slice(0, 2).toUpperCase()}</span>
                    )}
                    <div>
                      <strong>{product.name}</strong>
                      <small>{[product.brand, product.description].filter(Boolean).join(' · ')}</small>
                    </div>
                  </div>
                </td>
                <td data-label="SKU / código">
                  <code>{product.sku}</code>
                  <small>{product.barcode || 'Sem código'}</small>
                </td>
                <td data-label="Preço">{money(product.price_cents)}</td>
                <td data-label="Estoque">
                  {product.track_inventory
                    ? `${stockFor(data.balances, product.id, data.locations[0]?.id)} ${product.unit}`
                    : 'Livre'}
                </td>
                <td data-label="Tributário">
                  <span>{product.tax_code_ncm ? `NCM ${product.tax_code_ncm}` : 'Pendente'}</span>
                </td>
                <td data-label="Status">
                  <StatusBadge tone={product.active ? 'ok' : 'neutral'}>
                    {product.active ? 'Ativo' : 'Inativo'}
                  </StatusBadge>
                </td>
                {canEdit && (
                  <td>
                    <button className="iconButton subtle" onClick={() => open(product)}>
                      <Edit3 size={16} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!filtered.length && (
          <EmptyState
            icon={Boxes}
            title="Nenhum produto encontrado"
            description="Revise a busca ou cadastre o primeiro produto."
          />
        )}
      </section>

      {editing && (
        <Modal
          wide
          title={editing === 'new' ? 'Novo produto' : 'Editar produto'}
          subtitle="Alterações ficam vinculadas ao tenant e entram na trilha de auditoria."
          onClose={closeEditor}
          className="productWizardModal"
          footer={
            <>
              <div className="productDesktopActions">
                <Button kind="ghost" onClick={closeEditor}>Cancelar</Button>
                <Button disabled={busy || saveIssues.length>0 || Boolean(gtinWarning) || Boolean(openingIssue)} onClick={save}>
                  {busy?<Loader2 className="spin" size={17}/>:<Check size={17}/>} Salvar produto
                </Button>
              </div>
              <div className="productMobileActions">
                <Button kind="ghost" disabled={busy}
                  onClick={()=>mobileStep===0?closeEditor():goMobileStep(mobileStep-1)}>
                  {mobileStep===0?'Cancelar':'Voltar'}
                </Button>
                {mobileStep<3?
                  <Button onClick={()=>goMobileStep(mobileStep+1)}>Próximo <ChevronRight size={18}/></Button>:
                  <Button disabled={busy || saveIssues.length>0 || Boolean(gtinWarning) || Boolean(openingIssue)} onClick={save}>
                    {busy?<Loader2 className="spin" size={18}/>:<Check size={18}/>} Salvar produto
                  </Button>}
              </div>
            </>
          }
        >
          <nav className="productWizardProgress" aria-label="Etapas do cadastro">
            <div className="wizardProgressHeading"><strong>Cadastro guiado</strong><span>Etapa {mobileStep+1} de 4</span></div>
            <div className="wizardProgressTrack" aria-hidden="true">
              {wizardLabels.map((label,i)=><span key={label} className={i<=mobileStep?'filled':''}/>)}
            </div>
            <div className="wizardSteps">
              {wizardLabels.map((label,i)=><button type="button" key={label}
                className={i===mobileStep?'active':''}
                aria-current={i===mobileStep?'step':undefined} onClick={()=>goMobileStep(i)}>
                <span>{i+1}</span><small>{label}</small>
              </button>)}
            </div>
          </nav>
          <div className="formGrid productWizardGrid">
            <section className="productWizardStep" data-step="0" data-active={mobileStep===0}>
            <div className="span2">
              <ProductPhotoCapture
                preview={photoPreview}
                error={photoError}
                onSelect={selectPhoto}
              />
              <CatalogEnhancement form={form} setForm={setForm}
                photoFile={photoFile} photoPreview={photoPreview}
                extraPhotos={extraPhotos} setExtraPhotos={setExtraPhotos}
                onIdentify={onIdentify} categories={data.categories} products={data.products}
                onOpenExisting={open} mode="media"/>
            </div>
            </section>
            <section className="productWizardStep" data-step="1" data-active={mobileStep===1}>
            <Field label="Nome" className="span2">
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <Field label="Descrição" className="span2">
              <input
                value={form.description || ''}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </Field>
            <Field label="Marca">
              <input
                value={form.brand || ''}
                onChange={(event) => setForm({ ...form, brand: event.target.value })}
              />
            </Field>
            <Field label="Fornecedor">
              <input
                value={form.supplier || ''}
                onChange={(event) => setForm({ ...form, supplier: event.target.value })}
              />
            </Field>
            <div className="skuSmartField">
              <label htmlFor="pdv-master-sku">SKU / identificador interno</label>
              <div className="barcodeInputRow">
                <input id="pdv-master-sku" value={form.sku} placeholder={computedSku}
                  onChange={event=>setForm(current=>({...current,sku:event.target.value.toUpperCase()}))}/>
                <BarcodeCapture compact iconOnly
                  onDetected={code=>setForm(current=>({
                    ...current,barcode:code
                  }))}/>
              </div>
              <small>{form.sku?'SKU editável.':'SKU gerado automaticamente ao salvar.'} Fotografe o código para preencher GTIN e gerar o SKU correspondente.</small>
            </div>
            <div className="barcodeIntegrated">
              <label htmlFor="smartpdv-product-barcode">Código de barras</label>
              <div className="barcodeInputRow">
                <input id="smartpdv-product-barcode" inputMode="numeric"
                  placeholder="Digite ou fotografe" value={form.barcode || ''}
                  className={gtinWarning?'invalid':''}
                  onChange={event=>setForm(current=>({...current,barcode:normalizeBarcode(event.target.value)}))}/>
                <BarcodeCapture compact iconOnly
                  onDetected={code=>setForm(current=>({...current,barcode:code}))}/>
              </div>
              <small>{gtinWarning?'Dígito verificador do GTIN inválido.':'EAN, UPC, Code 128 ou código interno; digite ou fotografe.'}</small>
            </div>
            </section>
            <section className="productWizardStep" data-step="2" data-active={mobileStep===2}>
            <div className="categorySuggestField">
              <label htmlFor="pdv-category-select">Categoria</label>
              <select id="pdv-category-select"
                value={form.category_id || ''}
                onChange={event=>setForm(current=>({...current,category_id:event.target.value,metadata:{
                  ...(current.metadata||{}),classification:{...(current.metadata?.classification||{}),subcategory_id:''}
                }}))}>
                <option value="">Sem categoria</option>
                {roots.map(category=><option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
              {!form.category_id&&categorySuggestion&&!suggestedCategoryExists&&
                <button type="button" className="categorySuggestAction" disabled={categoryBusy}
                  onClick={()=>ensureCategory(categorySuggestion)}>
                  {categoryBusy?'Criando…':'Criar categoria sugerida: '+categorySuggestion}
                </button>}
              {!form.category_id&&categorySuggestion&&suggestedCategoryExists&&
                <small>Categoria sugerida já existe; selecione-a acima.</small>}
              <div className="categoryCreateRow">
                <input aria-label="Nome de nova categoria" placeholder="Ou digite nova categoria"
                  maxLength={80} value={categoryName} onChange={e=>setCategoryName(e.target.value)}/>
                <button type="button" disabled={categoryBusy||categoryName.trim().length<2}
                  onClick={()=>ensureCategory(categoryName)}>Adicionar</button>
              </div>
            </div>
            {form.category_id&&
              <div className="categorySuggestField">
                <label htmlFor="pdv-subcategory">Subcategoria</label>
                <select id="pdv-subcategory" value={selectedSubcategory}
                  onChange={event=>setForm(current=>({...current,metadata:{
                    ...(current.metadata||{}),classification:{
                      ...(current.metadata?.classification||{}),subcategory_id:event.target.value
                    }
                  }}))}>
                  <option value="">Sem subcategoria</option>
                  {subcategories.map(c=><option value={c.id} key={c.id}>{c.name}</option>)}
                </select>
                {!selectedSubcategory&&suggestedSubcategory&&!suggestedSubcategoryExists&&
                  <button type="button" className="categorySuggestAction" disabled={subBusy}
                    onClick={()=>ensureSubcategory(suggestedSubcategory)}>
                    {subBusy?'Criando…':'Criar subcategoria sugerida: '+suggestedSubcategory}
                  </button>}
                <div className="categoryCreateRow">
                  <input aria-label="Nova subcategoria" placeholder="Nova subcategoria"
                    maxLength={80} value={subcategoryName} onChange={event=>setSubcategoryName(event.target.value)}/>
                  <button type="button" disabled={subBusy||subcategoryName.trim().length<2}
                    onClick={()=>ensureSubcategory(subcategoryName)}>Adicionar</button>
                </div>
              </div>}
            <Field label="Unidade">
              <select
                value={form.unit}
                onChange={(event) => setForm({ ...form, unit: event.target.value })}
              >
                {['UN', 'KG', 'LT', 'ML', 'M', 'CX', 'PCT', 'SERV'].map((unit) => (
                  <option key={unit}>{unit}</option>
                ))}
              </select>
            </Field>
            <Field label="Preço de venda">
              <MoneyInput value={form.price} onChange={(value) => setForm({ ...form, price: value })} />
            </Field>
            <Field label="Custo">
              <MoneyInput value={form.cost} onChange={(value) => setForm({ ...form, cost: value })} />
            </Field>
            <Field label="Estoque mínimo" hint="Alerta de reposição. Não adiciona unidades ao estoque.">
              <input type="number" inputMode="decimal" min="0" step=".001"
                value={form.min_stock}
                onChange={event=>setForm(current=>({...current,min_stock:event.target.value}))}/>
            </Field>
            {isCreating&&form.track_inventory&&
              <Field label="Quantidade inicial disponível" className="initialStockField"
                hint="Unidades físicas existentes agora. Será lançada no histórico de estoque ao salvar, não é o estoque mínimo.">
                <input type="number" inputMode="decimal" min="0" max="1000000"
                  step={form.allow_fractional?'0.001':'1'} value={form.opening_stock}
                  onChange={event=>setForm(current=>({...current,opening_stock:event.target.value}))}/>
                {openingIssue&&<small role="alert" className="openingStockError">{openingIssue}</small>}
                {!canAdjustStock&&<small className="fieldHint">O lançamento positivo requer permissão de estoque.</small>}
              </Field>}
            {!isCreating&&form.track_inventory&&
              <div className="currentStockInfo">
                <strong>Saldo atual: {stockFor(data.balances,form.id,location?.id)} {form.unit}</strong>
                <span>Para adicionar, retirar ou corrigir quantidade, utilize Estoque → Ajustes. Esta edição não altera o saldo.</span>
              </div>}
            </section>
            <section className="productWizardStep" data-step="3" data-active={mobileStep===3}>
              <details className="wizardDisclosure" open={typeof window !== 'undefined' && window.matchMedia('(min-width: 761px)').matches}>
                <summary>Informações para vendas online <span>Opcional</span></summary>
                <CatalogEnhancement form={form} setForm={setForm}
                  photoFile={photoFile} photoPreview={photoPreview}
                  extraPhotos={extraPhotos} setExtraPhotos={setExtraPhotos}
                  onIdentify={onIdentify} categories={data.categories} mode="commerce"/>
              </details>
              <details className="wizardDisclosure" open={typeof window !== 'undefined' && window.matchMedia('(min-width: 761px)').matches}>
                <summary>Informações fiscais <span>Opcional · revisão humana</span></summary>
            <Field label="Origem tributária">
              <select
                value={form.tax_origin}
                onChange={(event) => setForm({ ...form, tax_origin: event.target.value })}
              >
                <option value="0">0 · Nacional</option>
                <option value="1">1 · Estrangeira direta</option>
                <option value="2">2 · Estrangeira mercado interno</option>
                <option value="3">3 · Nacional acima de 40% de importação</option>
                <option value="8">8 · Nacional acima de 70% de importação</option>
              </select>
            </Field>
            <div className="span2 fiscalProposal">
              <strong>Pesquisa assistida de classificação fiscal</strong>
              {fiscal?<div className="fiscalCandidate">
                <p><b>NCM candidato:</b> {fiscal.ncm} — {fiscal.ncm_label}</p>
                <p><b>CEST candidato:</b> {fiscal.cest} — {fiscal.cest_label}</p>
                <small>{fiscal.requires_confirmation} {fiscal.deprecated_warning}</small>
                <label className="fiscalConfirm">
                  <input type="checkbox" checked={fiscalAcknowledged}
                    onChange={e=>setFiscalAcknowledged(e.target.checked)}/>
                  Conferi a descrição e embalagem. Aplicar como proposta sujeita à validação fiscal.
                </label>
                <button type="button" disabled={!fiscalAcknowledged}
                  onClick={applyFiscalCandidate}>Usar sugestões no cadastro</button>
              </div>:<small>Sem correspondência segura nas regras verificadas. Consulte os documentos oficiais; não será inventado NCM ou CEST.</small>}
              <small>Fontes oficiais: <a href={FISCAL_SOURCES.ncm} rel="noreferrer" target="_blank">Receita Federal / TIPI</a>
                {' · '}<a href={FISCAL_SOURCES.cest} rel="noreferrer" target="_blank">CONFAZ / CEST</a>.
                Código sugerido não significa enquadramento homologado.</small>
            </div>
            <Field label="NCM" hint="8 dígitos; será usado somente pelo adapter fiscal futuro.">
              <input
                inputMode="numeric"
                maxLength="8"
                value={form.tax_code_ncm}
                onChange={(event) =>
                  setForm({
                    ...form,
                    tax_code_ncm: event.target.value.replace(/\D/g, '').slice(0, 8),
                  })
                }
              />
            </Field>
            <Field label="CEST" hint="Opcional, 7 dígitos.">
              <input
                inputMode="numeric"
                maxLength="7"
                value={form.tax_code_cest}
                onChange={(event) =>
                  setForm({
                    ...form,
                    tax_code_cest: event.target.value.replace(/\D/g, '').slice(0, 7),
                  })
                }
              />
            </Field>
              </details>
              <section className="channelReadiness" aria-label="Prontidão comercial por canal">
                <strong>Preparação para vendas e divulgação</strong>
                <small>Diagnóstico informativo, não publica nem conecta contas.</small>
                {[readiness.pdv,readiness.sales,readiness.publish,readiness.google,readiness.whatsapp,readiness.marketplace].map(channel=>
                  <div key={channel.key} className="channelReadinessRow">
                    <b>{channel.title}</b>
                    <span>{channel.status==='ready_for_review'?'Pronto para revisar':
                      channel.status==='not_connected'?'Canal não conectado':
                      channel.status==='media_review_required'?'Imagem de vitrine pendente':'Faltam: '+channel.missing.join(', ')}</span>
                  </div>
                )}
                <p>Imagem para canais: {readiness.image.approved_variant?'Versão aprovada':
                  readiness.image.premium_eligible?'Processamento premium disponível conforme provedor':
                  readiness.image.status==='pending_save'?'Fotos selecionadas: salve o produto para registrar as imagens privadas':
                  'Somente original; remover fundo e identidade visual aguardam habilitação/serviço real'}.</p>
              </section>
              <div className="wizardReviewNote" role="status">
                <strong>{form.name?.trim()||'Produto sem nome'}</strong>
                <span>SKU: {computedSku||'pendente'} · {form.barcode||'sem código'} · {form.category_id?'categoria selecionada':'sem categoria'}</span>
              </div>
            <label className="checkField">
              <input
                type="checkbox"
                checked={form.track_inventory}
                onChange={(event) => setForm({ ...form, track_inventory: event.target.checked })}
              />
              Controlar estoque
            </label>
            <label className="checkField">
              <input
                type="checkbox"
                checked={form.allow_fractional}
                onChange={(event) => setForm({ ...form, allow_fractional: event.target.checked })}
              />
              Permitir quantidade fracionada
            </label>
            <label className="checkField">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => setForm({ ...form, active: event.target.checked })}
              />
              Produto ativo
            </label>
            {(formError||saveIssues.length>0||gtinWarning)&&<div className="span2 saveIssues" role="alert">
              {formError||[...saveIssues,...(gtinWarning?['GTIN inválido.']:[])].join(' ')}
            </div>}
            </section>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Stock({ data, location, capabilities, onAdjust }) {
  const [product, setProduct] = useState('');
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('Contagem física');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [stockFilter, setStockFilter] = useState('all');
  const canAdjust = capability(capabilities, 'inventory');
  const products = data.products
    .filter((item) => item.track_inventory)
    .filter((item) => stockFilter === 'all' || (stockFilter === 'negative' ? stockFor(data.balances, item.id, location?.id) < 0 : stockFilter === 'zero' ? stockFor(data.balances, item.id, location?.id) === 0 : stockFor(data.balances, item.id, location?.id) <= Number(item.min_stock || 0)))
    .filter((item) =>
      [item.name, item.sku, item.barcode]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search.toLowerCase())),
    );

  const zeroCount=data.products.filter((item)=>item.active!==false&&item.track_inventory&&stockFor(data.balances,item.id,location?.id)===0).length;
  const negativeCount=data.products.filter((item)=>item.active!==false&&item.track_inventory&&stockFor(data.balances,item.id,location?.id)<0).length;
  const lowCount=data.products.filter((item)=>item.active!==false&&item.track_inventory&&stockFor(data.balances,item.id,location?.id)<=Number(item.min_stock||0)).length;
  function exportReport() {
    const safe=(value)=>{
      const s=String(value??'');
      const escaped=/^[=+@-]/.test(s)?"'"+s:s;
      return '"'+escaped.replace(/"/g,'""')+'"';
    };
    const csv=[['SKU','Produto','Código de barras','Saldo','Unidade','Estoque mínimo','Situação'].map(safe).join(';'),
      ...products.map((item)=>{
        const q=stockFor(data.balances,item.id,location?.id);
        return [item.sku,item.name,item.barcode,q,item.unit,item.min_stock,q<0?'Negativo':q===0?'Zerado':q<=Number(item.min_stock||0)?'Abaixo do mínimo':'Disponível'].map(safe).join(';');
      })].join('\r\n');
    const url=URL.createObjectURL(new Blob(['\uFEFF',csv],{type:'text/csv;charset=utf-8'}));
    const a=document.createElement('a');a.href=url;a.download='smartpdv-relatorio-estoque.csv';a.click();URL.revokeObjectURL(url);
  }

  async function adjust() {
    setBusy(true);
    try {
      await onAdjust({
        productId: product,
        quantityDelta: Number(delta),
        reason,
      });
      setDelta('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin">
      <Header
        eyebrow="ESTOQUE"
        title="Saldos auditáveis"
        description="Cada entrada, venda, ajuste e reversão gera movimento imutável."
        meta={
          <StatusBadge tone={data.settings.operations?.allow_negative_stock ? 'warning' : 'ok'}>
            {data.settings.operations?.allow_negative_stock
              ? 'Estoque negativo permitido'
              : 'Estoque negativo bloqueado'}
          </StatusBadge>
        }
      />
      <label className="sectionSearch">
        <Search size={18} />
        <input
          placeholder="Buscar produto ou código"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <div className="stockReportFilters stockReportTabs" role="group" aria-label="Filtrar saldo de estoque">
        <span>{zeroCount} zerados · {negativeCount} negativos · {lowCount} abaixo do mínimo</span>
        {[
          ['all','Todos'],['zero','Zerados'],['negative','Negativos'],['low','Abaixo do mínimo']
        ].map(([key,label])=><button key={key} type="button" aria-pressed={stockFilter===key}
          onClick={()=>setStockFilter(key)}>{label}</button>)}
        <button type="button" onClick={exportReport}>Exportar relatório CSV</button>
      </div>
      <div className="stockGrid">
        {products.map((item) => {
          const current = stockFor(data.balances, item.id, location?.id);
          const low = current <= Number(item.min_stock || 0);
          return (
            <article className={low ? 'low' : ''} key={item.id}>
              <Boxes size={23} />
              <div>
                <small>{item.sku}</small>
                <strong>{item.name}</strong>
                <span>{current < 0 ? 'Saldo negativo' : current === 0 ? 'Estoque zerado' : low ? 'Abaixo do mínimo' : 'Disponível'}</span>
              </div>
              <b>
                {current} {item.unit}
              </b>
            </article>
          );
        })}
      </div>

      {canAdjust && (
        <section className="panel formPanel">
          <div className="panelTitle">
            <div>
              <h2>Ajustar estoque</h2>
              <p>Use quantidade positiva para entrada e negativa para saída.</p>
            </div>
          </div>
          <div className="formGrid three">
            <Field label="Produto">
              <select value={product} onChange={(event) => setProduct(event.target.value)}>
                <option value="">Selecione</option>
                {data.products
                  .filter((item) => item.track_inventory)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Quantidade (+ / -)">
              <input
                type="number"
                step=".001"
                value={delta}
                onChange={(event) => setDelta(event.target.value)}
              />
            </Field>
            <Field label="Motivo">
              <input value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
          </div>
          <Button
            disabled={busy || !product || !Number(delta) || reason.trim().length < 3}
            onClick={adjust}
          >
            {busy ? <Loader2 className="spin" size={17} /> : <PackagePlus size={17} />}
            Registrar ajuste
          </Button>
        </section>
      )}

      <section className="panel">
        <div className="panelTitle">
          <div>
            <h2>Movimentos recentes</h2>
            <p>Saldo resultante, origem e horário.</p>
          </div>
        </div>
        {data.inventoryMovements.slice(0, 40).map((movement) => {
          const item = productById(data, movement.product_id);
          return (
            <div className="movementRow" key={movement.id}>
              <Activity size={17} />
              <div>
                <strong>{item?.name || movement.product_id}</strong>
                <small>
                  {translateMovement(movement.movement_type)} · {movement.reason || 'Sem motivo'} ·{' '}
                  {formatDateTime(movement.created_at)}
                </small>
              </div>
              <b className={Number(movement.quantity_delta) < 0 ? 'negative' : 'positive'}>
                {Number(movement.quantity_delta) > 0 ? '+' : ''}
                {movement.quantity_delta}
              </b>
              <span>Saldo {movement.balance_after}</span>
            </div>
          );
        })}
        {!data.inventoryMovements.length && (
          <EmptyState icon={Activity} title="Nenhum movimento registrado" />
        )}
      </section>
    </div>
  );
}

const EMPTY_CUSTOMER = {
  id: '',
  name: '',
  phone: '',
  email: '',
  tax_id: '',
  notes: '',
  marketing_consent: false,
};

export function Customers({ data, onSave }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_CUSTOMER });
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const filtered = data.customers.filter((customer) =>
    [customer.name, customer.phone, customer.email, customer.tax_id]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(search.toLowerCase())),
  );

  function open(customer) {
    setEditing(customer?.id || 'new');
    setForm({ ...EMPTY_CUSTOMER, ...(customer || {}) });
  }

  async function save() {
    setBusy(true);
    try {
      await onSave(form);
      setEditing(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin">
      <Header
        eyebrow="RELACIONAMENTO"
        title="Clientes"
        description="Cadastro mínimo por tenant, com consentimento e histórico comercial."
        action={
          <Button onClick={() => open(null)}>
            <UserPlus size={17} />
            Novo cliente
          </Button>
        }
      />
      <label className="sectionSearch">
        <Search size={18} />
        <input
          placeholder="Nome, telefone, e-mail ou CPF/CNPJ"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <section className="panel responsiveTable">
        <table>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Contato</th>
              <th>Documento</th>
              <th>Compras</th>
              <th>Marketing</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((customer) => {
              const sales = data.sales.filter(
                (sale) => sale.customer_id === customer.id && sale.status === 'completed',
              );
              return (
                <tr key={customer.id}>
                  <td data-label="Cliente">
                    <strong>{customer.name}</strong>
                    <small>{customer.notes || 'Sem observações'}</small>
                  </td>
                  <td data-label="Contato">
                    <span>{customer.phone || '—'}</span>
                    <small>{customer.email || '—'}</small>
                  </td>
                  <td data-label="Documento">{customer.tax_id || '—'}</td>
                  <td data-label="Compras">
                    <strong>{money(sales.reduce((sum, sale) => sum + sale.total_cents, 0))}</strong>
                    <small>{sales.length} venda(s)</small>
                  </td>
                  <td data-label="Marketing">
                    <StatusBadge tone={customer.marketing_consent ? 'ok' : 'neutral'}>
                      {customer.marketing_consent ? 'Consentiu' : 'Não'}
                    </StatusBadge>
                  </td>
                  <td>
                    <button className="iconButton subtle" onClick={() => open(customer)}>
                      <Edit3 size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!filtered.length && (
          <EmptyState
            icon={UsersRound}
            title="Nenhum cliente encontrado"
            description="Cadastre somente os dados necessários para a operação."
          />
        )}
      </section>

      {editing && (
        <Modal
          title={editing === 'new' ? 'Novo cliente' : 'Editar cliente'}
          subtitle="Consentimento de marketing é independente do cadastro da venda."
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button kind="ghost" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button disabled={busy || !form.name.trim()} onClick={save}>
                {busy ? <Loader2 className="spin" size={17} /> : <Check size={17} />}
                Salvar cliente
              </Button>
            </>
          }
        >
          <div className="formGrid">
            <Field label="Nome" className="span2">
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <Field label="Telefone">
              <input
                inputMode="tel"
                value={form.phone || ''}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
              />
            </Field>
            <Field label="E-mail">
              <input
                type="email"
                value={form.email || ''}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </Field>
            <Field label="CPF/CNPJ opcional">
              <input
                inputMode="numeric"
                value={form.tax_id || ''}
                onChange={(event) =>
                  setForm({ ...form, tax_id: event.target.value.replace(/\D/g, '').slice(0, 14) })
                }
              />
            </Field>
            <Field label="Observações">
              <input
                value={form.notes || ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </Field>
            <label className="checkField span2">
              <input
                type="checkbox"
                checked={Boolean(form.marketing_consent)}
                onChange={(event) =>
                  setForm({ ...form, marketing_consent: event.target.checked })
                }
              />
              Cliente autorizou comunicação de marketing
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Cash({
  data,
  session,
  capabilities,
  onOpen,
  onClose,
  onMove,
}) {
  const [opening, setOpening] = useState('0,00');
  const [movementOpen, setMovementOpen] = useState(null);
  const [amount, setAmount] = useState('0,00');
  const [reason, setReason] = useState('');
  const [closing, setClosing] = useState(null);
  const [declared, setDeclared] = useState('0,00');
  const [closingNotes, setClosingNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const expected = expectedCash(data.movements, session?.id);
  const declaredCents = centsFromDecimal(declared);
  const difference = declaredCents - expected;
  const canManage = capability(capabilities, 'cash_management');
  const canRegister = capability(capabilities, 'register') || canManage;

  async function move(type) {
    setBusy(true);
    try {
      await onMove({ type, amountCents: centsFromDecimal(amount), reason });
      setMovementOpen(null);
      setAmount('0,00');
    } finally {
      setBusy(false);
    }
  }

  async function close() {
    setBusy(true);
    try {
      await onClose({ declaredCents, notes: closingNotes });
      setClosing(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin">
      <Header
        eyebrow="CONTROLE DE CAIXA"
        title={session ? 'Caixa aberto' : 'Caixa fechado'}
        description="Abertura, recebimentos, sangria, suprimento e fechamento conciliado."
        action={
          session && canRegister ? (
            <Button
              kind="danger"
              onClick={() => {
                setDeclared(decimalFromCents(expected));
                setClosing(true);
              }}
            >
              Fechar caixa
            </Button>
          ) : null
        }
      />

      <section className="cashHero">
        <Store size={29} />
        <div>
          <small>SALDO ESPERADO EM DINHEIRO</small>
          <strong>{money(expected)}</strong>
          <span>
            {session
              ? `Aberto em ${formatDateTime(session.opened_at)}`
              : 'Nenhuma sessão aberta neste terminal'}
          </span>
        </div>
        {session && <StatusBadge tone="ok">Sessão ativa</StatusBadge>}
      </section>

      {!session && canRegister && (
        <section className="panel formPanel">
          <div className="panelTitle">
            <div>
              <h2>Abrir terminal</h2>
              <p>Informe o fundo inicial contado.</p>
            </div>
          </div>
          <div className="cashOpenForm">
            <Field label="Fundo inicial">
              <MoneyInput value={opening} onChange={setOpening} />
            </Field>
            <Button
              disabled={centsFromDecimal(opening) < 0}
              onClick={() => onOpen({ openingCents: centsFromDecimal(opening) })}
            >
              <LockKeyhole size={17} />
              Abrir caixa
            </Button>
          </div>
        </section>
      )}

      {session && canManage && (
        <section className="cashActionsPanel">
          <button
            onClick={() => {
              setMovementOpen('supply');
              setReason('Reforço de troco');
              setAmount('0,00');
            }}
          >
            <ArrowDownRight />
            <span>
              <strong>Suprimento</strong>
              <small>Adicionar dinheiro contado</small>
            </span>
          </button>
          <button
            onClick={() => {
              setMovementOpen('withdrawal');
              setReason('Sangria operacional');
              setAmount('0,00');
            }}
          >
            <ArrowUpRight />
            <span>
              <strong>Sangria</strong>
              <small>Retirar dinheiro com motivo</small>
            </span>
          </button>
        </section>
      )}

      <section className="panel">
        <div className="panelTitle">
          <div>
            <h2>Movimentações</h2>
            <p>Somente valores em dinheiro alteram o saldo esperado.</p>
          </div>
        </div>
        {data.movements
          .filter((movement) => !session || movement.session_id === session.id)
          .slice(0, 50)
          .map((movement) => (
            <div className="movementRow" key={movement.id}>
              <Activity size={17} />
              <div>
                <strong>{translateMovement(movement.movement_type)}</strong>
                <small>
                  {movement.reason || 'Sem motivo'} · {formatDateTime(movement.created_at)}
                </small>
              </div>
              <b
                className={
                  ['withdrawal', 'cancellation', 'refund'].includes(movement.movement_type)
                    ? 'negative'
                    : 'positive'
                }
              >
                {money(movement.amount_cents)}
              </b>
            </div>
          ))}
        {!data.movements.length && (
          <EmptyState icon={Activity} title="Nenhuma movimentação registrada" />
        )}
      </section>

      {movementOpen && (
        <Modal
          title={movementOpen === 'supply' ? 'Registrar suprimento' : 'Registrar sangria'}
          subtitle="A operação recebe chave idempotente e entra na trilha de auditoria."
          onClose={() => setMovementOpen(null)}
          footer={
            <>
              <Button kind="ghost" onClick={() => setMovementOpen(null)}>
                Cancelar
              </Button>
              <Button
                disabled={busy || centsFromDecimal(amount) <= 0 || reason.trim().length < 3}
                onClick={() => move(movementOpen)}
              >
                {busy ? <Loader2 className="spin" size={17} /> : <Check size={17} />}
                Confirmar
              </Button>
            </>
          }
        >
          <div className="formGrid">
            <Field label="Valor">
              <MoneyInput value={amount} onChange={setAmount} autoFocus />
            </Field>
            <Field label="Motivo">
              <input value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
          </div>
          {movementOpen === 'withdrawal' && centsFromDecimal(amount) > expected && (
            <p className="inlineError">A sangria não pode superar o dinheiro esperado.</p>
          )}
        </Modal>
      )}

      {closing && (
        <Modal
          title="Fechar caixa"
          className="cashCloseModal"
          subtitle="Conte o dinheiro físico. A diferença ficará registrada e não será apagada."
          onClose={() => setClosing(null)}
          footer={
            <>
              <Button kind="ghost" onClick={() => setClosing(null)}>
                Cancelar
              </Button>
              <Button disabled={busy || declaredCents < 0} onClick={close}>
                {busy ? <Loader2 className="spin" size={17} /> : <LockKeyhole size={17} />}
                Confirmar fechamento
              </Button>
            </>
          }
        >
          <div className="closeSummary">
            <div>
              <span>Esperado</span>
              <strong>{money(expected)}</strong>
            </div>
            <div>
              <span>Declarado</span>
              <MoneyInput value={declared} onChange={setDeclared} autoFocus />
            </div>
            <div className={difference === 0 ? 'balanced' : 'difference'}>
              <span>Diferença</span>
              <strong>{money(difference)}</strong>
            </div>
          </div>
          <Field label="Observação do fechamento">
            <textarea
              value={closingNotes}
              onChange={(event) => setClosingNotes(event.target.value)}
              placeholder={difference ? 'Explique a diferença encontrada' : 'Conferência concluída'}
            />
          </Field>
        </Modal>
      )}
    </div>
  );
}

export function Audit({ data, location }) {
  const missingStock = data.products.filter((product) =>
    product.active !== false && product.track_inventory &&
    stockFor(data.balances, product.id, location?.id) <= 0,
  );
  const [tab, setTab] = useState('sales');
  const tabs = [
    { value: 'sales', label: 'Vendas' },
    { value: 'inventory', label: 'Estoque' },
    { value: 'cash', label: 'Caixa' },
    { value: 'audit', label: 'Alterações' },
  ];

  return (
    <div className="admin">
      <Header
        eyebrow="EVIDÊNCIAS"
        title="Auditoria operacional"
        description="Trilha por tenant com eventos, operador e horário."
        meta={<StatusBadge tone="ok">Somente leitura</StatusBadge>}
      />
      <Segmented value={tab} onChange={setTab} options={tabs} />

      <section className="panel auditPanel">
        {tab === 'inventory' && (
          <div className="auditStockSummary">
            <strong>Relatório de estoque atual · {missingStock.length} sem estoque</strong>
            <small>Saldo registrado na unidade {location?.name || 'selecionada'}. Adições ao carrinho não alteram saldo; somente venda concluída ou ajuste movimenta o estoque.</small>
            {missingStock.map((product) => (
              <div className="auditRow" key={product.id}>
                <AlertTriangle size={17} />
                <div><strong>{product.name}</strong><small>Sem estoque · {product.sku}</small></div>
                <b>{stockFor(data.balances, product.id, location?.id)} {product.unit || 'UN'}</b>
              </div>
            ))}
          </div>
        )}
        {tab === 'sales' &&
          data.sales.map((sale) => (
            <div className="auditRow" key={sale.id}>
              <ReceiptText size={18} />
              <div>
                <strong>
                  Venda #{sale.sale_number} · {money(sale.total_cents)}
                </strong>
                <small>
                  {formatDateTime(sale.created_at)} · {sale.receipt_type || 'non_fiscal'} ·{' '}
                  {sale.status}
                </small>
              </div>
              <code>{String(sale.id).slice(0, 8)}</code>
            </div>
          ))}
        {tab === 'inventory' &&
          data.inventoryMovements.map((movement) => (
            <div className="auditRow" key={movement.id}>
              <Boxes size={18} />
              <div>
                <strong>
                  {productById(data, movement.product_id)?.name || movement.product_id} ·{' '}
                  {movement.quantity_delta}
                </strong>
                <small>
                  {translateMovement(movement.movement_type)} · saldo {movement.balance_after} ·{' '}
                  {formatDateTime(movement.created_at)}
                </small>
              </div>
              <code>{String(movement.id).slice(0, 8)}</code>
            </div>
          ))}
        {tab === 'cash' &&
          data.movements.map((movement) => (
            <div className="auditRow" key={movement.id}>
              <Banknote size={18} />
              <div>
                <strong>
                  {translateMovement(movement.movement_type)} · {money(movement.amount_cents)}
                </strong>
                <small>
                  {movement.reason || 'Sem motivo'} · {formatDateTime(movement.created_at)}
                </small>
              </div>
              <code>{String(movement.id).slice(0, 8)}</code>
            </div>
          ))}
        {tab === 'audit' &&
          data.auditEvents.map((event) => (
            <div className="auditRow" key={event.id}>
              <FileClock size={18} />
              <div>
                <strong>{event.event_type}</strong>
                <small>
                  {event.entity_type} · {event.entity_id} · {formatDateTime(event.created_at)}
                </small>
              </div>
              <code>{String(event.actor_id || 'system').slice(0, 8)}</code>
            </div>
          ))}
        {((tab === 'sales' && !data.sales.length) ||
          (tab === 'inventory' && !data.inventoryMovements.length && !missingStock.length) ||
          (tab === 'cash' && !data.movements.length) ||
          (tab === 'audit' && !data.auditEvents.length)) && (
          <EmptyState
            icon={FileClock}
            title="Nenhum evento nesta categoria"
            description="Novas operações aparecerão aqui."
          />
        )}
      </section>
    </div>
  );
}

function integrationState(settings, entitlement, key) {
  const config = settings.integrations?.[key] || {};
  const enabledByEntitlement =
    key === 'kpay'
      ? entitlement?.payment_capture_enabled
      : key === 'fiscal'
        ? entitlement?.fiscal_issuance_enabled
        : true;
  if (key === 'repair' && config.enabled) return { label: 'Ativo', tone: 'ok' };
  if (!enabledByEntitlement) return { label: 'Bloqueado por gate', tone: 'warning' };
  if (config.enabled && ['active', 'healthy', 'connected'].includes(config.status)) {
    return { label: 'Conectado', tone: 'ok' };
  }
  if (['adapter_ready', 'contract_ready'].includes(config.status)) {
    return { label: 'Adapter pronto', tone: 'neutral' };
  }
  return { label: 'Não configurado', tone: 'neutral' };
}

export function Settings({
  data,
  entitlement,
  capabilities,
  offlineCount,
  onSave,
  onSync,
  onThemePreview,
}) {
  const [form, setForm] = useState(() => structuredClone(data.settings));
  const [busy, setBusy] = useState(false);
  const canSave = capability(capabilities, 'settings');

  useEffect(() => setForm(structuredClone(data.settings)), [data.settings]);
  useEffect(() => () => onThemePreview?.(data.settings?.theme || {}), [data.settings?.theme, onThemePreview]);

  function nested(root, key, value) {
    setForm((current) => ({
      ...current,
      [root]: { ...current[root], [key]: value },
    }));
  }

  const integrations = [
    {
      key: 'kpay',
      name: 'K‑Pay',
      description: 'Captura e conciliação financeira',
    },
    {
      key: 'repair',
      name: 'Kaizen Repair',
      description: 'OS, produto e estoque compartilhados',
    },
    {
      key: 'fiscal',
      name: 'Fiscal',
      description: 'Adapter neutro, sem estado fixo',
    },
    {
      key: 'evo',
      name: 'EVO / K‑OS',
      description: 'Eventos e missões canônicas',
    },
  ];

  return (
    <div className="admin">
      <Header
        eyebrow="PERSONALIZAÇÃO POR TENANT"
        title="Configurações"
        description="Identidade e regras locais sem alterar o núcleo Kaizen."
        action={
          canSave && (
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onSave({
                    ...form,
                    operations: {
                      ...form.operations,
                      max_cashier_discount_percent:
                        Math.max(0, Math.min(100, Number(form.operations?.max_cashier_discount_percent || 0))),
                    },
                  });
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Loader2 className="spin" size={17} /> : <Settings2 size={17} />}
              {busy ? 'Salvando…' : 'Salvar alterações'}
            </Button>
          )
        }
      />

      <div className="settingsGrid">
        <section className="panel settingsPanel">
          <div className="panelTitle">
            <div>
              <h2>Identidade</h2>
              <p>Aplicada somente a este tenant.</p>
            </div>
          </div>
          <div className="formGrid">
            <Field label="Nome exibido" className="span2">
              <input
                disabled={!canSave}
                value={form.display_name || ''}
                onChange={(event) => setForm({ ...form, display_name: event.target.value })}
              />
            </Field>
            <Field
              label="Plano contratado"
              hint="Controlado pelo entitlement Kaizen; não pode ser elevado pelo tenant."
            >
              <input value={data.settings.plan_tier || 'initial'} disabled />
            </Field>
            <Field label="Tema">
              <select
                disabled={!canSave}
                value={form.theme?.mode || 'light'}
                onChange={(event) => {
                  const mode = event.target.value;
                  nested('theme', 'mode', mode);
                  onThemePreview?.({ ...(form.theme || {}), mode });
                }}
              >
                <option value="system">Sistema</option>
                <option value="dark">Escuro</option>
                <option value="light">Claro</option>
              </select>
            </Field>
            <Field label="Cor principal">
              <input
                type="color"
                disabled={!canSave}
                value={form.theme?.primary_color || '#635bff'}
                onChange={(event) => {
                  const primary_color = event.target.value;
                  nested('theme', 'primary_color', primary_color);
                  onThemePreview?.({ ...(form.theme || {}), primary_color });
                }}
              />
            </Field>
            <Field label="Cor de destaque">
              <input
                type="color"
                disabled={!canSave}
                value={form.theme?.accent_color || '#0891b2'}
                onChange={(event) => {
                  const accent_color = event.target.value;
                  nested('theme', 'accent_color', accent_color);
                  onThemePreview?.({ ...(form.theme || {}), accent_color });
                }}
              />
            </Field>
          </div>
        </section>

        <section className="panel settingsPanel">
          <div className="panelTitle">
            <div>
              <h2>Regras operacionais</h2>
              <p>Validadas novamente no banco.</p>
            </div>
          </div>
          <div className="formGrid">
            <Field label="Desconto máximo do operador (%)">
              <input
                type="number"
                min="0"
                max="100"
                disabled={!canSave}
                inputMode="decimal"
                aria-label="Desconto máximo do operador em porcentagem"
                value={form.operations?.max_cashier_discount_percent ?? ''}
                onFocus={(event) => event.currentTarget.select()}
                onChange={(event) =>
                  nested('operations', 'max_cashier_discount_percent', event.target.value === '' ? '' : Number(event.target.value))
                }
              />
            </Field>
            <Field label="Rodapé do recibo">
              <textarea
                disabled={!canSave}
                value={form.receipt?.footer_message || ''}
                onChange={(event) => nested('receipt', 'footer_message', event.target.value)}
              />
            </Field>
            <label className="checkField">
              <input
                type="checkbox"
                disabled={!canSave}
                checked={Boolean(form.operations?.allow_negative_stock)}
                onChange={(event) =>
                  nested('operations', 'allow_negative_stock', event.target.checked)
                }
              />
              Permitir estoque negativo
            </label>
            <label className="checkField">
              <input
                type="checkbox"
                disabled={!canSave}
                checked={Boolean(form.operations?.require_customer)}
                onChange={(event) => nested('operations', 'require_customer', event.target.checked)}
              />
              Exigir cliente na venda
            </label>
            <label className="checkField">
              <input
                type="checkbox"
                disabled={!canSave}
                checked={form.operations?.low_stock_notifications !== false}
                onChange={(event) =>
                  nested('operations', 'low_stock_notifications', event.target.checked)
                }
              />
              Alertas de estoque mínimo
            </label>
          </div>
        </section>

        <section className="panel integrationPanel">
          <div className="panelTitle">
            <div>
              <h2>Integrações desacopladas</h2>
              <p>Status operacional, não apenas existência do contrato.</p>
            </div>
          </div>
          <div className="integrationGrid">
            {integrations.map((integration) => {
              const state = integrationState(
                data.settings,
                entitlement || {},
                integration.key,
              );
              return (
                <article key={integration.key}>
                  <ShieldCheck size={22} />
                  <div>
                    <strong>{integration.name}</strong>
                    <small>{integration.description}</small>
                  </div>
                  <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                </article>
              );
            })}
          </div>
        </section>

        <section className="panel readinessPanel">
          <div className="panelTitle">
            <div>
              <h2>Prontidão e continuidade</h2>
              <p>O núcleo comercial continua não fiscal até homologação explícita.</p>
            </div>
          </div>
          <div className="readinessList">
            <div>
              <span>Emissão fiscal</span>
              <StatusBadge tone="warning">Bloqueada</StatusBadge>
            </div>
            <div>
              <span>Captura K‑Pay</span>
              <StatusBadge tone="warning">Bloqueada</StatusBadge>
            </div>
            <div>
              <span>Regras tributárias versionadas</span>
              <StatusBadge tone={data.taxRules.length ? 'ok' : 'neutral'}>
                {data.taxRules.length ? `${data.taxRules.length} regra(s)` : 'Estrutura pronta'}
              </StatusBadge>
            </div>
            <div>
              <span>Fila offline</span>
              <StatusBadge tone={offlineCount ? 'warning' : 'ok'}>
                {offlineCount ? `${offlineCount} pendente(s)` : 'Vazia'}
              </StatusBadge>
            </div>
          </div>
          {offlineCount > 0 && (
            <Button kind="ghost" onClick={onSync}>
              <RefreshCw size={17} />
              Sincronizar agora
            </Button>
          )}
        </section>
      </div>
    </div>
  );
}

export function ReceiptModal({ receipt, settings, onClose }) {
  if (!receipt) return null;
  const summary = totals(receipt.items, receipt.order_discount_cents || 0);
  const payments = receipt.payments || [];
  return (
    <Modal
      title={receipt.offline ? 'Venda na fila offline' : `Venda #${receipt.sale_number}`}
      subtitle={
        receipt.offline
          ? 'A venda será confirmada no servidor quando a conexão voltar.'
          : 'Operação concluída e registrada na trilha.'
      }
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            Fechar
          </Button>
          {!receipt.offline && (
            <Button onClick={() => window.print()}>
              <Printer size={17} />
              Imprimir recibo
            </Button>
          )}
        </>
      }
    >
      <article className="receiptPaper">
        <header>
          <strong>{settings.display_name}</strong>
          <span>K‑SmartPDV · documento não fiscal</span>
          <small>
            {receipt.offline ? `Provisório ${receipt.provisional_number}` : `Venda #${receipt.sale_number}`}
          </small>
        </header>
        <div className="receiptItems">
          {receipt.items.map((item) => (
            <div key={item.product_id}>
              <span>
                {item.quantity} × {item.name}
              </span>
              <b>
                {money(Math.round(item.quantity * item.unit_price_cents) - item.discount_cents)}
              </b>
            </div>
          ))}
        </div>
        <div className="receiptTotals">
          <div>
            <span>Subtotal</span>
            <b>{money(summary.subtotalCents)}</b>
          </div>
          <div>
            <span>Descontos</span>
            <b>- {money(summary.discountCents)}</b>
          </div>
          <div>
            <strong>Total</strong>
            <strong>{money(receipt.total_cents || summary.totalCents)}</strong>
          </div>
        </div>
        <div className="receiptPayments">
          {payments.map((payment, index) => (
            <span key={index}>
              {PAYMENT_LABELS[payment.method] || payment.method}: {money(payment.amount_cents)}
            </span>
          ))}
        </div>
        <footer>
          <p>{settings.receipt?.footer_message || 'Obrigado pela preferência!'}</p>
          <strong>NÃO É DOCUMENTO FISCAL</strong>
          <small>{receipt.created_at ? formatDateTime(receipt.created_at) : formatDateTime(new Date())}</small>
        </footer>
      </article>
    </Modal>
  );
}
